import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  PawPrint, Home, MapPin, FileText, FolderOpen,
  ShoppingBag, Lightbulb, Phone, Search, Calendar, CalendarDays,
  Settings, LogOut, Users, AlertTriangle, Shield, BarChart3, Dog
} from "lucide-react";

const navItems = [
  { path: "/dashboard", icon: Home, label: "Home" },
  { path: "/pets", icon: Dog, label: "I Miei Animali" },
  { path: "/calendar", icon: CalendarDays, label: "Calendario" },
  { path: "/documents", icon: FolderOpen, label: "Documenti" },
  { path: "/bookings", icon: Calendar, label: "Prenotazioni" },
  { path: "/marketplace", icon: MapPin, label: "Mappa" },
  { path: "/services", icon: Users, label: "Servizi" },
  { path: "/shop", icon: ShoppingBag, label: "Shop" },
  { path: "/tips", icon: Lightbulb, label: "Consigli" },
];

export default function SideNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const firstName = user?.name?.split(' ')[0] || 'Utente';
  const initials = user?.name 
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  return (
    <aside className="hidden lg:flex flex-col w-64 h-screen bg-white dark:bg-[#1C1C1E] border-r border-black/5 dark:border-white/10 fixed left-0 top-0 z-40">
      {/* Logo */}
      <div className="p-6 border-b border-black/5 dark:border-white/10">
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center">
            <PawPrint className="text-white" size={22} />
          </div>
          <span className="text-xl font-bold text-[#1D1D1F] dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Zampa
          </span>
        </button>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 min-h-0 p-4 overflow-y-auto">
        <div className="space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                  isActive 
                    ? 'bg-[#FF6B4A]/10 text-[#FF6B4A]' 
                    : 'text-[#6E6E73] dark:text-gray-400 hover:bg-[#F5F5F7] dark:hover:bg-white/5 hover:text-[#1D1D1F] dark:hover:text-white'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>

        {/* Emergency Buttons */}
        <div className="my-4 pt-4 border-t border-black/5 dark:border-white/10">
          <p className="px-4 py-2 text-xs font-semibold text-[#86868B] dark:text-gray-500 uppercase tracking-wider">
            Emergenza
          </p>
          <div className="space-y-2 mt-2">
            <NavLink
              to="/lost-pets"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                  isActive 
                    ? 'bg-amber-500 text-white' 
                    : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                }`
              }
              data-testid="nav-smarriti"
            >
              <Search size={20} />
              <span>Animale Smarrito</span>
            </NavLink>
            <NavLink
              to="/emergency"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                  isActive 
                    ? 'bg-red-500 text-white' 
                    : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                }`
              }
              data-testid="nav-emergenza"
            >
              <AlertTriangle size={20} />
              <span>Emergenza Veterinaria</span>
            </NavLink>
          </div>
        </div>

        {/* Admin Section */}
        {user?.is_admin && (
          <div className="my-4 pt-4 border-t border-black/5 dark:border-white/10">
            <p className="px-4 py-2 text-xs font-semibold text-[#FF6B4A] uppercase tracking-wider">
              Admin
            </p>
            <div className="space-y-1 mt-2">
              <NavLink
                to="/admin/professionals"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                    isActive 
                      ? 'bg-[#FF6B4A]/10 text-[#FF6B4A]' 
                      : 'text-[#6E6E73] dark:text-gray-400 hover:bg-[#F5F5F7] dark:hover:bg-white/5 hover:text-[#1D1D1F] dark:hover:text-white'
                  }`
                }
                data-testid="nav-admin-professionals"
              >
                <Shield size={20} />
                <span>Gestione Servizi</span>
              </NavLink>
              <NavLink
                to="/admin/dashboard"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                    isActive 
                      ? 'bg-[#FF6B4A]/10 text-[#FF6B4A]' 
                      : 'text-[#6E6E73] dark:text-gray-400 hover:bg-[#F5F5F7] dark:hover:bg-white/5 hover:text-[#1D1D1F] dark:hover:text-white'
                  }`
                }
                data-testid="nav-admin-dashboard"
              >
                <BarChart3 size={20} />
                <span>Dashboard Admin</span>
              </NavLink>
            </div>
          </div>
        )}
      </nav>

      {/* User Section - Clickable to go to Settings/Profile */}
      <div className="p-4 border-t border-black/5 dark:border-white/10">
        <button
          onClick={() => navigate('/settings')}
          className="w-full flex items-center gap-3 px-4 py-3 bg-[#F5F5F7] dark:bg-white/5 rounded-xl hover:bg-[#EBEBEB] dark:hover:bg-white/10 transition-colors text-left group"
          data-testid="user-profile-btn"
        >
          {/* User Photo or Initials */}
          {user?.picture ? (
            <img 
              src={user.picture} 
              alt={user.name || 'User'}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center text-white font-bold text-sm">
              {initials}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#1D1D1F] dark:text-white text-sm truncate group-hover:text-[#FF6B4A] transition-colors">
              {user?.name || firstName}
            </p>
            <p className="text-xs text-[#86868B] dark:text-gray-400 truncate">{user?.email}</p>
          </div>
          <Settings size={18} className="text-[#86868B] dark:text-gray-400 group-hover:text-[#FF6B4A] transition-colors" />
        </button>
        
        {/* Logout Button */}
        <button 
          onClick={handleLogout}
          className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[#86868B] dark:text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors text-sm font-medium"
          data-testid="logout-btn"
        >
          <LogOut size={16} />
          <span>Esci</span>
        </button>
      </div>
    </aside>
  );
}
